import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'
import { TableBackendPagination } from '@/components/shared/table-backend-pagination'
import { Muted } from '@/components/sv/typographies'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { EventDeliveryStatus } from '@/routes/webhook/tabs/event-deliveries/event-delivery-status'
import type { FilterType } from '@/routes/webhook/tabs/event-deliveries'
import type {
  WebhookDeliveriesQuery,
  WebhookDeliveriesQueryVariables,
} from '@/routes/webhook/tabs/event-deliveries/event-delivery-list/index.types.gen'

const WEBHOOK_EVENT_DELIVERIES = gql`
  query WebhookDeliveries(
    $idCompany: String!
    $id: String!
    $offset: Int
    $limit: Int
    $filters: JSON
  ) {
    company(id: $idCompany) {
      id
      webhook(id: $id) {
        id
        eventDeliveries(offset: $offset, limit: $limit, filters: $filters) {
          id
          total
          items {
            id
            type
            status
            responseStatus
            sentAt
            createdAt
          }
        }
      }
    }
  }
`

type Row = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<WebhookDeliveriesQuery['company']>['webhook']
    >['eventDeliveries']
  >['items']
>[0]

type Props = {
  filters: FilterType
}

export const EventDeliveryList = ({ filters = {} }: Props) => {
  const { idWebhook = '', idEventDelivery = '' } = useParams()
  const { idCompany } = useIdCompany()
  const { getCompanyPath, navigateToPath } = useCompanyPath()

  const [pagination, setPagination] = useState({ offset: 0, limit: 20 })

  const { data } = useSuspenseQuery<
    WebhookDeliveriesQuery,
    WebhookDeliveriesQueryVariables
  >(WEBHOOK_EVENT_DELIVERIES, {
    variables: {
      idCompany,
      id: idWebhook,
      offset: pagination.offset,
      limit: pagination.limit,
      filters,
    },
  })

  const items = data?.company?.webhook?.eventDeliveries?.items ?? []
  const total = data?.company?.webhook?.eventDeliveries?.total ?? 0

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <EventDeliveryStatus
              status={row.original.status}
              responseStatus={row.original.responseStatus ?? 'N/A'}
            />
            <div className="font-mono">{row.original.type}</div>
          </div>
        ),
      },
      {
        accessorKey: 'type',
        cell: ({ row }) => (
          <Muted className="flex justify-end">
            {format(
              row?.original?.sentAt
                ? row?.original?.sentAt
                : row?.original?.createdAt,
              'MMM dd, yyyy, HH:mm:ss'
            )}
          </Muted>
        ),
      },
    ],
    []
  )

  useEffect(() => {
    if (pagination.offset !== 0) {
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }
  }, [filters])

  useEffect(() => {
    if (!idEventDelivery && items.length > 0) {
      navigateToPath({
        pathname: '/webhook/:idWebhook/event-deliveries/:idEventDelivery',
        variables: { idWebhook, idEventDelivery: items[0].id },
        options: { replace: true },
      })
    }
  }, [idEventDelivery])

  const table = useReactTable({
    data: items,
    columns,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="mt-6 space-y-6">
      <Table>
        <TableBody>
          {table.getRowModel().rows?.length
            ? table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    'cursor-pointer',
                    idEventDelivery === row.original.id ? 'bg-muted' : ''
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="p-0">
                      <Link
                        to={getCompanyPath({
                          pathname:
                            '/webhook/:idWebhook/event-deliveries/:idEventDelivery',
                          variables: {
                            idWebhook,
                            idEventDelivery: row.original.id,
                          },
                        })}
                      >
                        <div className="px-2 py-1">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </Link>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!table.getRowModel().rows?.length ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No event found.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <TableBackendPagination
        offset={pagination.offset}
        limit={pagination.limit}
        total={total}
        handlePagination={({ offset, limit }) =>
          setPagination({ offset, limit })
        }
      />
    </div>
  )
}
